/**
 * liveCache.ts — a cache instance, played for real (P6b).
 *
 * ── THE ARCHITECTURE, IN ONE PARAGRAPH ────────────────────────────────────────
 * Entering a cache rides the NORMAL world pipeline. `switchSystem("cache:…")` already
 * works end to end: `systemDefFor` hands back the pad `SystemDef` (`cachePadSystem`),
 * `StarSystem` builds a real {@link Planet} over it — real sampler, real pose, real
 * quadtree terrain — and because the pad def is a 60 km barren sphere with 1.2 m of
 * relief, that planet IS a smooth ball. Nothing is torn down and nothing is stood up:
 * a cache is a system you fly (well, warp) into, and everything that already works on
 * a planet keeps working, because it IS a planet.
 *
 * This module adds the two things the pad def cannot carry, since layouts are
 * regenerated and never transmitted:
 *
 *   DRESSING   the theme's geometry, parented to `planet.group` at the +Y pole. The
 *              dressing draws in RENDER space (world − pole); planet-local space puts
 *              the pole at (0, R, 0), so ONE group offset converts the whole thing and
 *              the dressing then rides the planet's pose for free — the HomesteadMarkers
 *              precedent. Pad-local x/z ARE planet-local X/Z (the P4/P5b frame law), so
 *              the client's geometry and the server's drone seeding share a frame with
 *              no conversion at all.
 *   COLLIDERS  the theme's {@link BoxColliderField}, hung on `Planet.homesteadStandRadius`
 *              / `Planet.resolveHomesteadCollision` — the two PUBLIC assignable hooks
 *              `character.ts` already consults for standing and for wall push-out.
 *              `planet.settlementColliders` is the Planet's own private settlement field
 *              and is deliberately left alone.
 *
 * ⚠ THE HOOKS ARE ASSIGNED, NOT COMPOSED. In a cache instance homesteads cannot exist:
 * a beacon is authored world data attached to a real planet id, and a pad planet's id is
 * minted per instance. `main.ts` re-wires the homestead hooks on every `switchSystem`, so
 * this must run AFTER that (it does), and plain assignment is correct rather than merely
 * convenient. If homesteads ever become possible in an instance, this is the line to
 * change — compose, don't reassign.
 *
 * ⚠⚠ THE DATUM IS THE SAMPLED POLE, NOT `def.radius` (P8). The dressing is built on a
 * sphere of radius {@link padDatumRadius}, which is the pad planet's own terrain radius
 * at its +Y pole. Pinning it to `def.radius` — what P6b shipped — assumed the pad
 * planet's surface sits exactly on its nominal radius, and it does not: measured over
 * 60 instances the pole's terrain sits **−21 m to +16 m** from `def.radius` (the local
 * relief across a layout footprint is a mild 1.4 m median; the OFFSET is what bites).
 * The player's ground is `sampler.surfaceRadius` (`Planet.surfaceRadiusWorld`) and the
 * server seeds its garrison on the same sampler, so a dressing pinned to the nominal
 * radius was the only thing in the instance standing somewhere else — walls buried to
 * their tops, or floating out of reach, depending on the instance.
 *
 * ⚠⚠ FLOORS ARE STILL FLAT LIVE, and `CacheRoom.floorH` (P8, shared) is NOT the last
 * piece. Both sides of the live world take their ground from the pad planet's sampler:
 * the player through `CharacterController.clampToGround`, the drones through
 * `DroneField`'s per-planet `SurfaceFn`. Neither knows about terraces, so honouring
 * floorH live means giving BOTH a cache floor field — a client stand hook and a server
 * SurfaceFn override over the same shared floor query — not just un-setting a flag.
 * Terracing one side alone would separate players from drones by up to the ladder's
 * full range (17.7 m measured on tier 1). The layout data is in place for that work;
 * until then `flat: true` keeps one datum for everybody.
 */
import * as THREE from "three";
import {
  BoxColliderField,
  CACHE_PAD_RADIUS,
  buildBoxField,
  cacheBoxGroups,
  cacheWallBoxes,
  generateCache,
  type CacheLayoutV1,
  type CacheStateMsg,
  type CacheTheme,
  type CacheWallSet,
} from "@hyperlight/shared";
import type { Planet } from "../world/planet";
import type { CacheDressing } from "./theme/common";
import { dressRuin } from "./theme/ruin";
import { dressVault } from "./theme/vault";
import { dressHollow } from "./theme/hollow";

/** The regeneration inputs `cacheState` carries — everything needed to rebuild a run. */
export interface CacheInstanceInfo {
  /** `cache:<site>:<key>:<epoch>` — the system id this instance is played in. */
  instance: string;
  site: string;
  epoch: number;
  theme: CacheTheme;
  tier: number;
}

/** Read a `cacheState` frame into the regeneration inputs, or null if it is malformed. */
export function cacheInfoOf(msg: CacheStateMsg): CacheInstanceInfo | null {
  if (typeof msg.instance !== "string" || typeof msg.site !== "string") return null;
  if (!Number.isFinite(msg.epoch) || !Number.isFinite(msg.tier)) return null;
  const theme = msg.theme;
  if (theme !== "ruin" && theme !== "vault" && theme !== "hollow") return null;
  return { instance: msg.instance, site: msg.site, epoch: msg.epoch, theme, tier: msg.tier };
}

/**
 * The pad planet's own terrain radius at its +Y pole — THE datum a live instance is
 * built on (see the header for why `def.radius` is not it). Pure `f(def)`: the server
 * reaches the same number through the same shared sampler, which is what keeps the
 * garrison and the geometry in one frame.
 */
export function padDatumRadius(planet: Pick<Planet, "sampler" | "def">): number {
  return planet.sampler ? planet.sampler.surfaceRadius(0, 1, 0) : planet.def.radius;
}

/** Dress a layout with its theme. The one place the three dressers are selected. */
export function dressForTheme(
  layout: CacheLayoutV1, theme: CacheTheme, opts: { radius?: number; visuals?: boolean; flat?: boolean },
): CacheDressing {
  if (theme === "vault") return dressVault(layout, opts);
  if (theme === "hollow") return dressHollow(layout, opts);
  return dressRuin(layout, opts);
}

/**
 * One dressed, collidable cache instance living on a real {@link Planet}.
 *
 * Construction is the whole install: regenerate the layout, dress it flat, parent the
 * group at the pole and hang the collider field on the planet's hooks. {@link dispose}
 * is the exact inverse, so `switchSystem` can build and drop these freely.
 */
export class LiveCache {
  readonly layout: CacheLayoutV1;
  readonly dressing: CacheDressing;
  /** Gate edges currently open — mirrors the server's `prog.gates`. */
  private readonly open = new Set<number>();
  private readonly poleOffset: number;
  /**
   * THE BOLT FIELD — the canonical shared boxes (`cacheWallBoxes`), folded here and
   * NOT the theme's own dressing field.
   *
   * ⚠ IT MUST BE THE SHARED SET, not `dressing.colliders`. The server owns bolt
   * resolution and folds exactly these boxes (`server/src/cacheWalls.ts`); a client
   * prediction folded from the dressing would stop bolts at a vault's ceiling slabs
   * or a hollow's rock prisms — solids the server has never heard of — and the
   * player would watch bolts die in mid-air and then take damage through them. The
   * dressing field stays the CHARACTER's collision, where being generous is right.
   */
  private readonly boltBoxes: CacheWallSet;
  private boltField: BoxColliderField;

  constructor(readonly planet: Planet, readonly info: CacheInstanceInfo) {
    this.layout = generateCache({
      site: info.site, epoch: info.epoch, theme: info.theme, tier: info.tier,
    });
    // The radius the dressing must be built for: the collider boxes are placed at
    // planet-local |p| = R + h, and a mismatch puts every solid at the wrong altitude.
    // SAMPLED from the live planet, never assumed — see the header.
    const R = padDatumRadius(planet);
    this.poleOffset = R;
    this.dressing = dressForTheme(this.layout, info.theme, { radius: R, flat: true });
    this.boltBoxes = cacheWallBoxes(this.layout);
    this.boltField = this.foldBolts();
    // Render space (world − pole) → planet-local: the pole is (0, R, 0).
    this.dressing.group.position.set(0, R, 0);
    planet.group.add(this.dressing.group);
    this.wireColliders();
  }

  /** Re-fold the bolt field for the current gate set (no removal seam — see server). */
  private foldBolts(): BoxColliderField {
    for (const g of this.boltBoxes.gates) g.open = this.open.has(g.edgeIndex);
    // Walls AND canonical lids (`cacheBoxGroups`) — byte-identical to the server's
    // fold in server/src/cacheWalls.ts, which is the whole point of the shared set.
    return buildBoxField(this.poleOffset, cacheBoxGroups(this.boltBoxes), this.boltBoxes.gates);
  }

  /** Hang the current collider field on the planet's public solid-geometry hooks. */
  private wireColliders(): void {
    const field = this.dressing.colliders;
    this.planet.homesteadStandRadius = (x, y, z) => field.standRadius(x, y, z);
    this.planet.resolveHomesteadCollision = (x, y, z, r, out) => field.resolve(x, y, z, r, out);
    // The CAMERA's read of the same field (P8). Without it the boom only tested the
    // planet's own settlement boxes — none, on a pad world — so it walked its full
    // length through vault masonry and hollow rock. Vault/hollow ceilings answer this
    // query too, which is the point: under a roof the camera should pull in.
    this.planet.homesteadOccludes = (x, y, z, r) => field.occludes(x, y, z, r);
    // The BOLT's read (2026-07-27). Deliberately the shared field, not `field` —
    // see the `boltBoxes` doc. Without this a cache wall stopped the player but
    // not their gunfire, in either direction.
    this.planet.homesteadSegmentHit = (ax, ay, az, bx, by, bz, r) =>
      this.boltField.segmentHit(ax, ay, az, bx, by, bz, r);
  }

  /** Pad-local (x, z) of a PLANET-LOCAL point — the identity the frame law promises. */
  padLocalOf(local: THREE.Vector3): [number, number] {
    return [local.x, local.z];
  }

  /** PLANET-LOCAL point at pad-local (x, z), seated on the pad sphere. */
  localPointAt(x: number, z: number, out = new THREE.Vector3()): THREE.Vector3 {
    const rr = this.poleOffset + this.dressing.heightAt(x, z);
    return out.set(x, Math.sqrt(Math.max(0, rr * rr - x * x - z * z)), z);
  }

  /** The room a run starts (and ends) in. */
  get entranceRoom(): CacheLayoutV1["rooms"][number] {
    return this.layout.rooms[this.layout.entrance];
  }

  /** PLANET-LOCAL spawn point: the entrance room's centre, on the ground. */
  entranceLocal(out = new THREE.Vector3()): THREE.Vector3 {
    const r = this.entranceRoom;
    return this.localPointAt(r.x, r.z, out);
  }

  /** A facing that looks from the entrance toward the layout's middle, so a player
   *  arrives pointed INTO the dungeon rather than at whatever wall is behind them. */
  entranceFacing(): [number, number, number] {
    const r = this.entranceRoom;
    const dx = -r.x, dz = -r.z;
    const l = Math.hypot(dx, dz);
    return l < 1e-3 ? [0, 0, 1] : [dx / l, 0, dz / l];
  }

  /**
   * PLANET-LOCAL centre of wall box `i`, at mid-height — the exact point a solid-geometry
   * query MUST answer true at. The probe seam for "are the colliders actually live on
   * the planet", which is the silent-failure this feature is most exposed to (a dressing
   * you walk straight through looks perfectly fine until you try to walk into it).
   */
  wallProbe(i: number, out = new THREE.Vector3()): THREE.Vector3 {
    const b = this.dressing.walls[i];
    const rr = this.poleOffset + b.baseH + b.height * 0.5;
    return out.set(b.cx, Math.sqrt(Math.max(0, rr * rr - b.cx * b.cx - b.cz * b.cz)), b.cz);
  }

  /** Lateral distance (metres) from a PLANET-LOCAL point to the entrance room centre —
   *  the exit prompt's range test. Lateral, matching every other cache proximity gate. */
  distanceToEntrance(local: THREE.Vector3): number {
    const r = this.entranceRoom;
    return Math.hypot(local.x - r.x, local.z - r.z);
  }

  /**
   * Apply the server's run progress. Gates are the only thing with geometry behind
   * them; the client NEVER opens one of its own accord (P5's rule) — this is purely
   * "the server said so, make the world agree".
   *
   * GATE PARITY. The server opens a gate inside `CacheInstanceService.progress`, in
   * the same call that rebuilds ITS wall field, and broadcasts `cacheState`; the
   * client rebuilds here when that frame lands. So for one broadcast window the
   * server's barrier is down while the client's is still up. That divergence is
   * ACCEPTED and is the right way round: the authority is already permissive, so the
   * worst case is a predicted bolt of your own dying in a doorway the server let
   * through — never a hit you take through a barrier you can see.
   */
  applyProg(prog: CacheStateMsg["prog"]): void {
    const want = new Set(prog.gates ?? []);
    let changed = false;
    for (const g of this.dressing.gates) {
      const shouldOpen = want.has(g.edgeIndex);
      if (shouldOpen === this.open.has(g.edgeIndex)) continue;
      this.dressing.setGateOpen(g.edgeIndex, shouldOpen);
      if (shouldOpen) this.open.add(g.edgeIndex);
      else this.open.delete(g.edgeIndex);
      changed = true;
    }
    // setGateOpen REBUILDS the field (it has no removal seam), so the hooks must be
    // re-pointed at the new one or the player keeps colliding with the old gate.
    if (changed) {
      this.boltField = this.foldBolts();
      this.wireColliders();
    }
  }

  /** Probe/diagnostic summary — surfaced on `window.__hl.caches`. */
  describe(): {
    instance: string; site: string; theme: CacheTheme; tier: number; cycle: string;
    rooms: number; walls: number; gates: number; gatesOpen: number[]; drawn: number;
    /** The sampled datum the dressing was built on, and the pad's nominal radius —
     *  their difference is the offset P6b's `def.radius` anchor ignored (see header). */
    datum: number; nominal: number;
    /** Ladder range in the LAYOUT (shared data). Live dressing flattens it — the
     *  gap between this and the ground is the remaining terrace work. */
    floorSpan: number;
  } {
    let hi = 0;
    for (const r of this.layout.rooms) if (r.floorH > hi) hi = r.floorH;
    return {
      instance: this.info.instance,
      site: this.info.site,
      theme: this.info.theme,
      tier: this.layout.tier,
      cycle: this.layout.cycle,
      datum: this.poleOffset,
      nominal: this.planet.def.radius,
      floorSpan: hi,
      rooms: this.layout.rooms.length,
      walls: this.dressing.walls.length,
      gates: this.dressing.gates.length,
      gatesOpen: [...this.open].sort((a, b) => a - b),
      drawn: this.dressing.group.children.length,
    };
  }

  dispose(): void {
    this.dressing.group.parent?.remove(this.dressing.group);
    this.dressing.dispose();
    // Hand the planet back exactly as found. It is about to be disposed with its
    // system in every real call path, but a hook left pointing at a dead field is the
    // kind of thing that survives one refactor and crashes on the next.
    this.planet.homesteadStandRadius = null;
    this.planet.resolveHomesteadCollision = null;
    this.planet.homesteadOccludes = null;
    this.planet.homesteadSegmentHit = null;
  }
}

/** Pad radius the client assumes when no live def is at hand (probes, diagnostics). */
export const LIVE_PAD_RADIUS = CACHE_PAD_RADIUS;
